import { Cloud, Clouds, Hud, PerspectiveCamera } from '@react-three/drei'
import { useFrame } from '@react-three/fiber'
import { useLayoutEffect, useRef, useState } from 'react'
import { Group, Mesh, MeshBasicMaterial } from 'three'

import { GamePhase, useGameStore } from '@stores'
import { useLevelControls, usePostProcessingControls } from '@utils'

const INTRO_COVER_DELAY = 1.5
const COVER_DURATION = 1.5
const HOLD_DURATION = 0.5
const REVEAL_DURATION = 3.0

function easeInOut(t: number) {
  return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t
}

type Mode = 'intro-revealing' | 'waiting' | 'covering' | 'revealing' | 'idle'

type AnimProps = { opacity: number; xOff: number; yOff: number; scale: number }

const S = 8

function computeAnimProps(mode: Mode, t: number, introT: number, revealT: number): AnimProps {
  let opacity = 0
  let xOff = S * 0.22
  let yOff = S * 0.22
  let scale = 2.2

  if (mode === 'intro-revealing') {
    const rt = easeInOut(Math.max(0, Math.min((introT - HOLD_DURATION) / REVEAL_DURATION, 1)))
    opacity = 1 - rt
    xOff = rt * S * 0.22
    yOff = rt * S * 0.22
    scale = 2.2 - rt * 1.2
  } else if (mode === 'covering') {
    const coverT = easeInOut(Math.max(0, Math.min(t / COVER_DURATION, 1)))
    opacity = Math.min(coverT * 1.3, 1)
    xOff = S * 0.22 * (1 - coverT)
    yOff = S * 0.22 * (1 - coverT)
    scale = 1.0 + coverT * 1.2
  } else if (mode === 'revealing') {
    const rt = easeInOut(Math.max(0, Math.min((revealT - HOLD_DURATION) / REVEAL_DURATION, 1)))
    opacity = 1 - rt
    xOff = rt * S * 0.22
    yOff = rt * S * 0.22
    scale = 2.2 - rt * 1.2
  }

  return { opacity, xOff, yOff, scale }
}

export function CloudTransition() {
  const { clouds } = useLevelControls()
  const { enabled: postProcessingEnabled } = usePostProcessingControls()

  const [mode, setMode] = useState<Mode>('intro-revealing')

  const t = useRef(0)
  const revealT = useRef(0)
  const introT = useRef(0)
  const materialsRef = useRef<MeshBasicMaterial[]>([])
  const opacityRef = useRef(-1)

  const cloudWrapperRef = useRef<Group>(null)
  const cloud1Ref = useRef<Group>(null)
  const cloud2Ref = useRef<Group>(null)
  const cloud3Ref = useRef<Group>(null)
  const cloud4Ref = useRef<Group>(null)

  useLayoutEffect(() => {
    const materials = new Set<MeshBasicMaterial>()
    cloudWrapperRef.current?.traverse(obj => {
      if ((obj as Mesh).isMesh) {
        materials.add((obj as Mesh).material as MeshBasicMaterial)
      }
    })
    materialsRef.current = [...materials]

    if (mode === 'idle') return
    const { opacity } = computeAnimProps(mode, t.current, introT.current, revealT.current)
    opacityRef.current = opacity
    for (const material of materialsRef.current) {
      material.opacity = opacity
    }
  }, [mode])

  function applyProps({ opacity, xOff, yOff, scale }: AnimProps) {
    if (opacity !== opacityRef.current) {
      opacityRef.current = opacity
      for (const material of materialsRef.current) {
        // eslint-disable-next-line react-hooks/immutability
        material.opacity = opacity
      }
    }
    if (cloud1Ref.current) {
      cloud1Ref.current.position.set(-xOff, yOff, 0)
      cloud1Ref.current.scale.setScalar(scale)
    }
    if (cloud2Ref.current) {
      cloud2Ref.current.position.set(xOff, yOff, 0)
      cloud2Ref.current.scale.setScalar(scale)
    }
    if (cloud3Ref.current) {
      cloud3Ref.current.position.set(-xOff, -yOff, 1)
      cloud3Ref.current.scale.setScalar(scale * 0.95)
    }
    if (cloud4Ref.current) {
      cloud4Ref.current.position.set(xOff, -yOff, 1)
      cloud4Ref.current.scale.setScalar(scale * 0.95)
    }
  }

  useFrame((_, delta) => {
    const { levelComplete, forceTransition, phase, nextLevel, setPhase } = useGameStore.getState()

    if (mode === 'intro-revealing') {
      if (phase === GamePhase.LOADING) return
      introT.current += delta
      if (introT.current >= HOLD_DURATION + REVEAL_DURATION) {
        setMode('idle')
        return
      }
      applyProps(computeAnimProps('intro-revealing', t.current, introT.current, revealT.current))
      return
    }

    if (mode === 'idle') {
      if (levelComplete) {
        if (phase === GamePhase.TITLE) {
          setPhase(GamePhase.TITLE_TRANSITION)
          setTimeout(() => setMode('waiting'), INTRO_COVER_DELAY * 1000)
        } else if (phase === GamePhase.LEVEL_SELECTION) {
          setPhase(GamePhase.LEVEL_SELECTION_TRANSITION)
          setTimeout(() => setMode('waiting'), INTRO_COVER_DELAY * 1000)
        } else if (phase === GamePhase.PLAYING) {
          setPhase(GamePhase.LEVEL_TRANSITION)
          setMode('waiting')
        }
      } else if (forceTransition) {
        t.current = 0
        setMode('covering')
      }
      return
    }

    if (mode === 'waiting') {
      const loco = useGameStore.getState().network.locomotive
      const locoExited = !loco || loco.edgeId.startsWith('ESCAPE:')
      if (locoExited) {
        t.current = 0
        setMode('covering')
      }
      return
    }

    if (mode === 'covering') {
      t.current += delta
      if (t.current >= COVER_DURATION) {
        nextLevel()
        revealT.current = 0
        setMode('revealing')
        return
      }
      applyProps(computeAnimProps('covering', t.current, introT.current, revealT.current))
      return
    }

    // revealing
    revealT.current += delta
    if (revealT.current >= HOLD_DURATION + REVEAL_DURATION) {
      setMode('idle')
      return
    }
    applyProps(computeAnimProps('revealing', t.current, introT.current, revealT.current))
  })

  const s = 8
  const bX = s * 0.8
  const bY = s * 0.8
  const { xOff, yOff, scale } = computeAnimProps(mode, 0, 0, 0)
  const isVisible = mode !== 'idle'

  return (
    clouds && (
      <Hud renderPriority={postProcessingEnabled ? 2 : 1}>
        <PerspectiveCamera makeDefault position={[0, 0, 5]} />
        <group ref={cloudWrapperRef} visible={isVisible}>
          <Clouds material={MeshBasicMaterial} frustumCulled={false} renderOrder={10}>
            <group ref={cloud1Ref} position={[-xOff, yOff, 0]} scale={scale}>
              <Cloud seed={10} bounds={[bX, bY, 0.5]} volume={10} />
            </group>
            <group ref={cloud2Ref} position={[xOff, yOff, 0]} scale={scale}>
              <Cloud seed={20} bounds={[bX, bY, 0.5]} volume={10} />
            </group>
            <group ref={cloud3Ref} position={[-xOff, -yOff, 1]} scale={scale * 0.95}>
              <Cloud seed={30} bounds={[bX, bY, 0.5]} volume={10} />
            </group>
            <group ref={cloud4Ref} position={[xOff, -yOff, 1]} scale={scale * 0.95}>
              <Cloud seed={40} bounds={[bX, bY, 0.5]} volume={10} />
            </group>
          </Clouds>
        </group>
      </Hud>
    )
  )
}
